import React, { useCallback, useEffect, useState } from 'react'
import {
    App,
    Button,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
} from 'antd'
import {
    CheckCircleOutlined,
    EditOutlined,
    PlusOutlined,
    SafetyCertificateOutlined,
    SearchOutlined,
    StopOutlined,
    DeleteOutlined,
    StarOutlined,
} from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import { useTranslation } from 'react-i18next'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { usePermissions } from '../../hooks/usePermissions'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { extractApiErrorDetail } from '../../utils/apiErrorI18n'
import {
    PAYMENT_MERCHANT_CREATE_ACCESS_PERMISSIONS,
    PAYMENT_MERCHANT_DELETE_ACCESS_PERMISSIONS,
    PAYMENT_MERCHANT_SET_DEFAULT_ACCESS_PERMISSIONS,
    PAYMENT_MERCHANT_STATUS_UPDATE_ACCESS_PERMISSIONS,
    PAYMENT_MERCHANT_UPDATE_ACCESS_PERMISSIONS,
    PAYMENT_MERCHANT_VALIDATE_ACCESS_PERMISSIONS,
} from '../../utils/permissions'
import {
    activatePaymentMerchant,
    createPaymentMerchant,
    deactivatePaymentMerchant,
    deletePaymentMerchant,
    getPaymentMerchant,
    getPaymentMerchants,
    PaymentMerchant,
    PaymentMerchantDraftValidationResult,
    PaymentMerchantFormData,
    setPaymentMerchantFallbackDefault,
    updatePaymentMerchant,
    validatePaymentMerchant,
    validatePaymentMerchantDraft,
} from '../../services/paymentMerchantService'

const getChannelLabel = (
    channelCode: string,
    t: (key: string) => string
): string => {
    const normalized = channelCode.trim().toUpperCase()

    if (normalized === 'FUIOU') {
        return t('admin:paymentMerchant.channels.fuiou')
    }

    return normalized
}

const renderEllipsisText = (value: unknown): React.ReactNode => {
    const text = typeof value === 'string' && value.trim() ? value : '-'

    return (
        <span
            style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
            }}
            title={text}
        >
            {text}
        </span>
    )
}
const MATERIAL_FIELDS = new Set([
    'merchant_code',
    'merchant_secret',
    'base_url',
    'notify_url',
    'version',
    'timeout_seconds',
    'extra_config_text',
])
const DEFAULT_PAGE_SIZE = 10

type DraftValidationState =
    | 'not_validated'
    | 'validating'
    | 'validated'
    | 'validation_failed'

interface PaymentMerchantFilters {
    channel?: string
}

const DEFAULT_PAYMENT_MERCHANT_FILTERS: PaymentMerchantFilters = {
    channel: undefined,
}

const getPaymentMerchantErrorDetail = (
    error: unknown,
    fallback: string
): string => {
    const detail = extractApiErrorDetail(error)
    if (detail) {
        return detail
    }
    return error instanceof Error ? error.message : fallback
}

const translateMerchantMessage = (
    message: string | null | undefined,
    t: (key: string) => string
): string => {
    if (!message) {
        return ''
    }

    const normalized = message.trim()

    const exactMap: Record<string, string> = {
        'payment merchant not found': t(
            'admin:paymentMerchant.feedback.notFound'
        ),
        'merchant code already exists for this channel': t(
            'admin:paymentMerchant.feedback.duplicateMerchantCode'
        ),
        'payment merchant is already referenced by payment attempts': t(
            'admin:paymentMerchant.feedback.referencedByAttempts'
        ),
        'payment merchant is already referenced by payment refunds': t(
            'admin:paymentMerchant.feedback.referencedByRefunds'
        ),
        'fallback default merchant cannot be deactivated': t(
            'admin:paymentMerchant.feedback.fallbackCannotDeactivate'
        ),
        'fallback default merchant must be active': t(
            'admin:paymentMerchant.feedback.fallbackMustBeActive'
        ),
        'fallback default merchant must be validated successfully': t(
            'admin:paymentMerchant.feedback.fallbackMustBeValidated'
        ),
        'unsupported payment channel': t(
            'admin:paymentMerchant.feedback.unsupportedChannel'
        ),
        'merchant secret updated, please validate again': t(
            'admin:paymentMerchant.feedback.secretUpdatedRevalidate'
        ),
        'merchant configuration updated, please validate again': t(
            'admin:paymentMerchant.feedback.configUpdatedRevalidate'
        ),
        'merchant configuration updated, please validate again; fallback default cleared':
            t('admin:paymentMerchant.feedback.configUpdatedFallbackCleared'),
        'merchant secret updated, please validate again; fallback default cleared':
            t('admin:paymentMerchant.feedback.secretUpdatedFallbackCleared'),
        'merchant configuration must be validated before saving': t(
            'admin:paymentMerchant.feedback.mustValidateBeforeSave'
        ),
        'merchant validation token is missing or expired': t(
            'admin:paymentMerchant.feedback.validationTokenExpired'
        ),
        'merchant configuration changed after validation, please validate again':
            t('admin:paymentMerchant.feedback.validationConfigChanged'),
        'fallback default cleared': t(
            'admin:paymentMerchant.feedback.fallbackCleared'
        ),
        'Validation failed: payment provider is unreachable or request timed out':
            t('admin:paymentMerchant.feedback.validationProviderUnavailable'),
        'Validation failed: payment provider returned HTTP error': t(
            'admin:paymentMerchant.feedback.validationHttpError'
        ),
        'Validation failed: payment provider returned invalid response': t(
            'admin:paymentMerchant.feedback.validationInvalidResponse'
        ),
        'Validation failed: specified history order was not found': t(
            'admin:paymentMerchant.feedback.validationHistoryOrderNotFound'
        ),
        'Validation failed: invalid merchant credentials or signature rejected':
            t('admin:paymentMerchant.feedback.validationCredentialRejected'),
        'Validation passed: provider reachable, probe order not found': t(
            'admin:paymentMerchant.feedback.validationProbeOrderNotFound'
        ),
        'Validation passed: history order query succeeded': t(
            'admin:paymentMerchant.feedback.validationHistoryOrderSucceeded'
        ),
        'Validation passed: provider reachable and signature accepted': t(
            'admin:paymentMerchant.feedback.validationBasicSucceeded'
        ),
    }

    if (exactMap[normalized]) {
        return exactMap[normalized]
    }

    if (normalized.startsWith('unsupported payment channel:')) {
        return t('admin:paymentMerchant.feedback.unsupportedChannelWithCode')
    }

    if (
        normalized.startsWith('Validation failed: provider validation failed:')
    ) {
        return t('admin:paymentMerchant.feedback.validationProviderRejected')
    }

    if (normalized.startsWith('Validation failed:')) {
        return t('admin:paymentMerchant.feedback.validationFailedGeneric')
    }

    return normalized
}

const PaymentMerchantManagement: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin', 'navigation', 'common'])
    const { hasAnyPermissions } = usePermissions()
    const canCreate = hasAnyPermissions(
        PAYMENT_MERCHANT_CREATE_ACCESS_PERMISSIONS
    )
    const canUpdate = hasAnyPermissions(
        PAYMENT_MERCHANT_UPDATE_ACCESS_PERMISSIONS
    )
    const canValidate = hasAnyPermissions(
        PAYMENT_MERCHANT_VALIDATE_ACCESS_PERMISSIONS
    )
    const canUpdateStatus = hasAnyPermissions(
        PAYMENT_MERCHANT_STATUS_UPDATE_ACCESS_PERMISSIONS
    )
    const canSetDefault = hasAnyPermissions(
        PAYMENT_MERCHANT_SET_DEFAULT_ACCESS_PERMISSIONS
    )
    const canDelete = hasAnyPermissions(
        PAYMENT_MERCHANT_DELETE_ACCESS_PERMISSIONS
    )
    const channelOptions = [
        { label: getChannelLabel('FUIOU', t), value: 'FUIOU' },
    ]

    const [items, setItems] = useState<PaymentMerchant[]>([])
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [editingMerchant, setEditingMerchant] =
        useState<PaymentMerchant | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [channelFilter, setChannelFilter] = useState<string | undefined>(
        undefined
    )
    const [appliedFilters, setAppliedFilters] =
        useState<PaymentMerchantFilters>({
            ...DEFAULT_PAYMENT_MERCHANT_FILTERS,
        })
    const [queryTrigger, setQueryTrigger] = useState(0)
    const [draftValidationState, setDraftValidationState] =
        useState<DraftValidationState>('not_validated')
    const [draftValidationResult, setDraftValidationResult] =
        useState<PaymentMerchantDraftValidationResult | null>(null)
    const [validationSnapshot, setValidationSnapshot] = useState<string>('')
    const [hasMaterialChanges, setHasMaterialChanges] = useState(false)
    const [openDeleteMerchantId, setOpenDeleteMerchantId] = useState<
        number | null
    >(null)
    const [form] = Form.useForm<
        PaymentMerchantFormData & {
            merchant_secret?: string
            extra_config_text?: string
        }
    >()

    const resetDraftValidationState = useCallback(() => {
        setDraftValidationState('not_validated')
        setDraftValidationResult(null)
        setValidationSnapshot('')
    }, [])

    const buildValidationSnapshot = useCallback(
        (
            values: PaymentMerchantFormData & {
                merchant_secret?: string
                extra_config_text?: string
            }
        ) =>
            JSON.stringify({
                channel_code: values.channel_code?.trim().toUpperCase() || '',
                merchant_code: values.merchant_code?.trim() || '',
                merchant_secret: values.merchant_secret?.trim() || '',
                channel_order_no_prefix:
                    values.channel_order_no_prefix?.trim().toUpperCase() || '',
                base_url: values.base_url?.trim() || '',
                notify_url: values.notify_url?.trim() || '',
                version: values.version?.trim() || '',
                timeout_seconds: values.timeout_seconds ?? null,
                extra_config_text: values.extra_config_text?.trim() || '{}',
            }),
        []
    )

    const loadMerchants = useCallback(async () => {
        void queryTrigger

        setLoading(true)
        try {
            const data = await getPaymentMerchants(appliedFilters.channel)
            setItems(
                data.map((merchant) => ({
                    ...merchant,
                    validation_message: translateMerchantMessage(
                        merchant.validation_message,
                        t
                    ),
                }))
            )
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.loadFailed')
                    ),
                    t
                )
            )
        } finally {
            setLoading(false)
        }
    }, [appliedFilters, message, queryTrigger, t])

    useEffect(() => {
        void loadMerchants()
    }, [loadMerchants])

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(items.length / pageSize))
        setCurrentPage((page) => Math.min(page, maxPage))
    }, [items.length, pageSize])

    const handleResetFilters = () => {
        setCurrentPage(1)
        setChannelFilter(undefined)
        setAppliedFilters({ ...DEFAULT_PAYMENT_MERCHANT_FILTERS })
    }

    const handleApplyFilters = () => {
        setCurrentPage(1)
        setAppliedFilters({ channel: channelFilter })
        setQueryTrigger((value) => value + 1)
    }

    const openCreateModal = () => {
        setEditingMerchant(null)
        setHasMaterialChanges(false)
        resetDraftValidationState()
        form.resetFields()
        form.setFieldsValue({
            channel_code: 'FUIOU',
            version: '1.0',
            timeout_seconds: 15,
            channel_order_no_prefix: '',
            is_active: false,
            is_fallback_default: false,
            extra_config_text: '{}',
        })
        setModalOpen(true)
    }

    const openEditModal = async (merchant: PaymentMerchant) => {
        try {
            const detail = await getPaymentMerchant(merchant.id)
            setEditingMerchant(detail)
            setHasMaterialChanges(false)
            form.setFieldsValue({
                channel_code: detail.channel_code,
                merchant_name: detail.merchant_name,
                merchant_code: detail.merchant_code,
                channel_order_no_prefix: detail.channel_order_no_prefix || '',
                base_url: detail.base_url,
                notify_url: detail.notify_url,
                version: detail.version,
                timeout_seconds: detail.timeout_seconds,
                is_active: detail.is_active,
                is_fallback_default: detail.is_fallback_default,
                merchant_secret: '',
                extra_config_text: JSON.stringify(
                    detail.extra_config || {},
                    null,
                    2
                ),
            })
            if (detail.validation_status === 'VALID') {
                setDraftValidationState('validated')
            } else {
                setDraftValidationState('not_validated')
            }
            setDraftValidationResult(null)
            setValidationSnapshot('')
            setModalOpen(true)
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.detailLoadFailed')
                    ),
                    t
                )
            )
        }
    }

    const handleValuesChange = (
        changedValues: Partial<
            PaymentMerchantFormData & {
                merchant_secret?: string
                extra_config_text?: string
            }
        >,
        allValues: PaymentMerchantFormData & {
            merchant_secret?: string
            extra_config_text?: string
        }
    ) => {
        const changedKeys = Object.keys(changedValues)
        if (!changedKeys.some((field) => MATERIAL_FIELDS.has(field))) {
            return
        }

        const isMaterialChange = editingMerchant
            ? buildValidationSnapshot(allValues) !==
              JSON.stringify({
                  channel_code: editingMerchant.channel_code,
                  merchant_code: editingMerchant.merchant_code,
                  merchant_secret: '',
                  channel_order_no_prefix:
                      editingMerchant.channel_order_no_prefix || '',
                  base_url: editingMerchant.base_url,
                  notify_url: editingMerchant.notify_url,
                  version: editingMerchant.version,
                  timeout_seconds: editingMerchant.timeout_seconds,
                  extra_config_text: JSON.stringify(
                      editingMerchant.extra_config || {},
                      null,
                      2
                  ),
              })
            : true

        setHasMaterialChanges(isMaterialChange)

        if (draftValidationState === 'validated') {
            const nextSnapshot = buildValidationSnapshot(allValues)
            if (nextSnapshot !== validationSnapshot) {
                resetDraftValidationState()
            }
        }
    }

    const handleDraftValidate = async () => {
        try {
            const values = await form.validateFields()
            const payload = {
                merchant_id: editingMerchant?.id,
                channel_code: values.channel_code.trim().toUpperCase(),
                merchant_name: values.merchant_name,
                merchant_code: values.merchant_code.trim(),
                merchant_secret: values.merchant_secret,
                channel_order_no_prefix:
                    values.channel_order_no_prefix?.trim().toUpperCase() ||
                    undefined,
                base_url: values.base_url,
                notify_url: values.notify_url,
                version: values.version,
                timeout_seconds: values.timeout_seconds,
                extra_config: values.extra_config_text
                    ? JSON.parse(values.extra_config_text)
                    : {},
                is_active: values.is_active,
                is_fallback_default: false,
                probe_mode: 'basic' as const,
            }
            setDraftValidationState('validating')
            const result = await validatePaymentMerchantDraft(payload)
            setDraftValidationResult(result)
            setValidationSnapshot(buildValidationSnapshot(values))
            setDraftValidationState('validated')
            message.success(
                translateMerchantMessage(result.validation_message, t)
            )
        } catch (error) {
            if (error instanceof SyntaxError) {
                message.error(
                    t('admin:paymentMerchant.messages.invalidExtraConfig')
                )
                setDraftValidationState('validation_failed')
                return
            }
            const translated = translateMerchantMessage(
                getPaymentMerchantErrorDetail(
                    error,
                    t('admin:paymentMerchant.messages.validateFailed')
                ),
                t
            )
            setDraftValidationState('validation_failed')
            setDraftValidationResult(null)
            message.error(translated)
        }
    }

    const canSaveWithoutNewValidation =
        Boolean(editingMerchant) &&
        editingMerchant?.validation_status === 'VALID' &&
        !hasMaterialChanges

    const canSubmit =
        draftValidationState === 'validated' || canSaveWithoutNewValidation

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            const normalizedChannelCode = values.channel_code
                .trim()
                .toUpperCase()
            const normalizedMerchantCode = values.merchant_code.trim()
            const duplicateMerchant = items.find(
                (item) =>
                    item.channel_code === normalizedChannelCode &&
                    item.merchant_code === normalizedMerchantCode &&
                    item.id !== editingMerchant?.id
            )

            if (duplicateMerchant) {
                message.error(
                    t('admin:paymentMerchant.feedback.duplicateMerchantCode')
                )
                return
            }

            const payload = {
                channel_code: normalizedChannelCode,
                merchant_name: values.merchant_name,
                merchant_code: normalizedMerchantCode,
                merchant_secret: values.merchant_secret,
                channel_order_no_prefix:
                    values.channel_order_no_prefix?.trim().toUpperCase() ||
                    undefined,
                base_url: values.base_url,
                notify_url: values.notify_url,
                version: values.version,
                timeout_seconds: values.timeout_seconds,
                is_active: values.is_active,
                extra_config: values.extra_config_text
                    ? JSON.parse(values.extra_config_text)
                    : {},
                validation_token: draftValidationResult?.validation_token,
            }

            if (!canSubmit) {
                message.error(
                    t('admin:paymentMerchant.feedback.mustValidateBeforeSave')
                )
                return
            }

            if (editingMerchant) {
                await updatePaymentMerchant(
                    editingMerchant.id,
                    canSaveWithoutNewValidation
                        ? { ...payload, validation_token: undefined }
                        : payload
                )
                message.success(
                    t('admin:paymentMerchant.messages.updateSuccess')
                )
            } else {
                await createPaymentMerchant(
                    payload as PaymentMerchantFormData & {
                        merchant_secret: string
                        validation_token: string
                    }
                )
                message.success(
                    t('admin:paymentMerchant.messages.createSuccess')
                )
            }

            setModalOpen(false)
            form.resetFields()
            resetDraftValidationState()
            setHasMaterialChanges(false)
            await loadMerchants()
        } catch (error) {
            if (error instanceof SyntaxError) {
                message.error(
                    t('admin:paymentMerchant.messages.invalidExtraConfig')
                )
                return
            }
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.operationFailed')
                    ),
                    t
                )
            )
        }
    }

    const handleValidate = async (merchant: PaymentMerchant) => {
        try {
            const result = await validatePaymentMerchant(merchant.id, {
                probe_mode: 'basic',
            })
            message[result.success ? 'success' : 'warning'](
                translateMerchantMessage(result.validation_message, t)
            )
            await loadMerchants()
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.validateFailed')
                    ),
                    t
                )
            )
        }
    }

    const handleToggleActive = async (merchant: PaymentMerchant) => {
        try {
            if (merchant.is_active) {
                await deactivatePaymentMerchant(merchant.id)
            } else {
                await activatePaymentMerchant(merchant.id)
            }
            message.success(t('admin:paymentMerchant.messages.statusUpdated'))
            await loadMerchants()
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.operationFailed')
                    ),
                    t
                )
            )
        }
    }

    const handleSetDefault = async (merchant: PaymentMerchant) => {
        try {
            await setPaymentMerchantFallbackDefault(merchant.id)
            message.success(
                t('admin:paymentMerchant.messages.setDefaultSuccess')
            )
            await loadMerchants()
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.operationFailed')
                    ),
                    t
                )
            )
        }
    }

    const handleDelete = async (merchant: PaymentMerchant) => {
        try {
            await deletePaymentMerchant(merchant.id)
            message.success(t('admin:paymentMerchant.messages.deleteSuccess'))
            await loadMerchants()
        } catch (error) {
            message.error(
                translateMerchantMessage(
                    getPaymentMerchantErrorDetail(
                        error,
                        t('admin:paymentMerchant.messages.deleteFailed')
                    ),
                    t
                )
            )
        }
    }

    const actionColumnStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, max-content)',
        alignItems: 'center',
        columnGap: 12,
        rowGap: 8,
        maxWidth: '100%',
    }
    const actionLinkButtonStyle: React.CSSProperties = {
        paddingInline: 4,
        height: 24,
        lineHeight: '24px',
        whiteSpace: 'normal',
    }

    const columns: TableColumnsType<PaymentMerchant> = [
        {
            title: t('admin:paymentMerchant.columns.channel'),
            dataIndex: 'channel_code',
            key: 'channel_code',
            width: 120,
            ellipsis: true,
            render: (value: unknown) => (
                <span title={typeof value === 'string' ? value : undefined}>
                    {typeof value === 'string' ? getChannelLabel(value, t) : '-'}
                </span>
            ),
        },
        {
            title: t('admin:paymentMerchant.columns.name'),
            dataIndex: 'merchant_name',
            key: 'merchant_name',
            width: 240,
            ellipsis: true,
            render: renderEllipsisText,
        },
        {
            title: t('admin:paymentMerchant.columns.code'),
            dataIndex: 'merchant_code',
            key: 'merchant_code',
            width: 190,
            ellipsis: true,
            render: renderEllipsisText,
        },
        {
            title: t('admin:paymentMerchant.columns.orderPrefix'),
            dataIndex: 'channel_order_no_prefix',
            key: 'channel_order_no_prefix',
            width: 140,
            ellipsis: true,
            render: renderEllipsisText,
        },
        {
            title: t('admin:paymentMerchant.columns.status'),
            key: 'status',
            width: 120,
            render: (_: unknown, record: PaymentMerchant) => (
                <Space wrap>
                    <Tag color={record.is_active ? 'green' : 'default'}>
                        {record.is_active
                            ? t('admin:paymentMerchant.status.active')
                            : t('admin:paymentMerchant.status.inactive')}
                    </Tag>
                    {record.is_fallback_default ? (
                        <Tag color="gold">
                            {t('admin:paymentMerchant.status.fallbackDefault')}
                        </Tag>
                    ) : null}
                </Space>
            ),
        },
        {
            title: t('admin:paymentMerchant.columns.validation'),
            key: 'validation_status',
            width: 220,
            render: (_: unknown, record: PaymentMerchant) => {
                const color =
                    record.validation_status === 'VALID'
                        ? 'green'
                        : record.validation_status === 'INVALID'
                          ? 'red'
                          : 'default'
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color={color}>
                            {t(
                                `admin:paymentMerchant.validation.${record.validation_status.toLowerCase()}`
                            )}
                        </Tag>
                        {record.validation_message ? (
                            <span
                                style={{
                                    display: 'inline-block',
                                    maxWidth: 220,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'bottom',
                                }}
                                title={record.validation_message}
                            >
                                {record.validation_message}
                            </span>
                        ) : null}
                    </Space>
                )
            },
        },
        {
            title: t('admin:paymentMerchant.columns.actions'),
            key: 'actions',
            width: 320,
            fixed: 'right',
            render: (_: unknown, record: PaymentMerchant) => (
                <div style={actionColumnStyle}>
                    {canUpdate ? (
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => void openEditModal(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('admin:paymentMerchant.actions.edit')}
                        </Button>
                    ) : null}
                    {canValidate ? (
                        <Button
                            type="link"
                            icon={<SafetyCertificateOutlined />}
                            onClick={() => void handleValidate(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('admin:paymentMerchant.actions.validate')}
                        </Button>
                    ) : null}
                    {canSetDefault ? (
                        <Button
                            type="link"
                            icon={<StarOutlined />}
                            onClick={() => void handleSetDefault(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t(
                                'admin:paymentMerchant.actions.setFallbackDefault'
                            )}
                        </Button>
                    ) : null}
                    {canUpdateStatus ? (
                        <Button
                            type="link"
                            danger={record.is_active}
                            icon={
                                record.is_active ? (
                                    <StopOutlined />
                                ) : (
                                    <CheckCircleOutlined />
                                )
                            }
                            onClick={() => void handleToggleActive(record)}
                            style={actionLinkButtonStyle}
                        >
                            {record.is_active
                                ? t('admin:paymentMerchant.actions.deactivate')
                                : t('admin:paymentMerchant.actions.activate')}
                        </Button>
                    ) : null}
                    {canDelete ? (
                        <Popconfirm
                            title={t(
                                'admin:paymentMerchant.messages.confirmDelete'
                            )}
                            open={openDeleteMerchantId === record.id}
                            onOpenChange={(open) => {
                                if (open) {
                                    setOpenDeleteMerchantId(record.id)
                                }
                            }}
                            onCancel={() => setOpenDeleteMerchantId(null)}
                            onConfirm={() => {
                                setOpenDeleteMerchantId(null)
                                void handleDelete(record)
                            }}
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                style={actionLinkButtonStyle}
                            >
                                {t('admin:paymentMerchant.actions.delete')}
                            </Button>
                        </Popconfirm>
                    ) : null}
                </div>
            ),
        },
    ]

    return (
        <PageLayout
            title={t('admin:paymentMerchant.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.paymentCenter') },
                {
                    title: t('navigation:menu.paymentMerchantManagement'),
                    path: '/admin/payment-merchants',
                },
            ]}
        >
            <FilterBar>
                <FilterField label={t('admin:paymentMerchant.filters.channel')}>
                    <Select
                        allowClear
                        aria-label={t('admin:paymentMerchant.filters.channel')}
                        placeholder={t(
                            'admin:paymentMerchant.filters.channel'
                        )}
                        options={channelOptions}
                        value={channelFilter}
                        onChange={setChannelFilter}
                    />
                </FilterField>
                <FilterActions>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleApplyFilters}
                        loading={loading}
                    >
                        {t('common:buttons.search')}
                    </Button>
                    <Button onClick={handleResetFilters}>
                        {t('common:buttons.reset')}
                    </Button>
                </FilterActions>
            </FilterBar>
            <p>{t('admin:paymentMerchant.messages.validateHelp')}</p>
            <PaginatedListContainer>
                {canCreate ? (
                    <PaginatedListToolbar>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreateModal}
                        >
                            {t('common:buttons.add')}
                        </Button>
                    </PaginatedListToolbar>
                ) : null}
                <PaginatedListCard>
                    <Table
                        rowKey="id"
                        loading={loading}
                        dataSource={items}
                        columns={columns}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        scroll={{ x: 1230 }}
                        pagination={buildPaginatedListPagination({
                            current: currentPage,
                            pageSize,
                            total: items.length,
                            showTotal: (count) =>
                                t('admin:paymentMerchant.pagination.total', {
                                    total: count,
                                }),
                            onChange: (page, size) => {
                                setCurrentPage(page)
                                setPageSize(size)
                            },
                        })}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                open={modalOpen}
                title={
                    editingMerchant
                        ? t('admin:paymentMerchant.modal.editTitle')
                        : t('admin:paymentMerchant.modal.createTitle')
                }
                onCancel={() => {
                    setModalOpen(false)
                    resetDraftValidationState()
                    setHasMaterialChanges(false)
                }}
                onOk={() => void handleSubmit()}
                okButtonProps={{ disabled: !canSubmit }}
                maskClosable={false}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleValuesChange}
                >
                    <Form.Item
                        name="channel_code"
                        label={t('admin:paymentMerchant.form.channel')}
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={channelOptions}
                            disabled={Boolean(editingMerchant)}
                        />
                    </Form.Item>
                    <Form.Item
                        name="merchant_name"
                        label={t('admin:paymentMerchant.form.name')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="merchant_code"
                        label={t('admin:paymentMerchant.form.code')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="channel_order_no_prefix"
                        label={t('admin:paymentMerchant.form.orderPrefix')}
                        dependencies={['is_active', 'is_fallback_default']}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator: async (
                                    _,
                                    value: string | undefined
                                ) => {
                                    const normalized = value?.trim()
                                    const requiresPrefix =
                                        !editingMerchant ||
                                        Boolean(getFieldValue('is_active')) ||
                                        Boolean(
                                            getFieldValue('is_fallback_default')
                                        )
                                    if (requiresPrefix && !normalized) {
                                        throw new Error(
                                            t(
                                                'admin:paymentMerchant.feedback.orderPrefixRequired'
                                            )
                                        )
                                    }
                                },
                            }),
                            {
                                pattern: /^[A-Z0-9_-]+$/,
                                message: t(
                                    'admin:paymentMerchant.feedback.invalidOrderPrefix'
                                ),
                            },
                            {
                                max: 32,
                                message: t(
                                    'admin:paymentMerchant.feedback.orderPrefixTooLong'
                                ),
                            },
                        ]}
                        normalize={(value: string | undefined) =>
                            value?.trim().toUpperCase()
                        }
                    >
                        <Input
                            placeholder={t(
                                'admin:paymentMerchant.form.orderPrefixPlaceholder'
                            )}
                        />
                    </Form.Item>
                    <Form.Item
                        name="merchant_secret"
                        label={t('admin:paymentMerchant.form.secret')}
                        rules={editingMerchant ? [] : [{ required: true }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="base_url"
                        label={t('admin:paymentMerchant.form.baseUrl')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="notify_url"
                        label={t('admin:paymentMerchant.form.notifyUrl')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="version"
                        label={t('admin:paymentMerchant.form.version')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="timeout_seconds"
                        label={t('admin:paymentMerchant.form.timeout')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            min={1}
                            max={120}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="extra_config_text"
                        label={t('admin:paymentMerchant.form.extraConfig')}
                    >
                        <Input.TextArea rows={6} />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label={t('admin:paymentMerchant.form.active')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button
                            type="default"
                            icon={<SafetyCertificateOutlined />}
                            loading={draftValidationState === 'validating'}
                            onClick={() => void handleDraftValidate()}
                        >
                            {t('admin:paymentMerchant.actions.validate')}
                        </Button>
                        {draftValidationResult?.validation_message ? (
                            <Tag
                                color={
                                    draftValidationState === 'validated'
                                        ? 'green'
                                        : 'red'
                                }
                            >
                                {translateMerchantMessage(
                                    draftValidationResult.validation_message,
                                    t
                                )}
                            </Tag>
                        ) : editingMerchant?.validation_status === 'VALID' &&
                          !hasMaterialChanges ? (
                            <Tag color="green">
                                {t(
                                    'admin:paymentMerchant.feedback.validationAlreadyValid'
                                )}
                            </Tag>
                        ) : null}
                    </Space>
                </Form>
            </Modal>
        </PageLayout>
    )
}

export default PaymentMerchantManagement
